/**
 * DAFNE Pattern Analysis Engine
 *
 * Analyzes CGM and diary data to detect patterns and provide
 * DAFNE-aligned recommendations for insulin adjustments.
 */

import { subDays, startOfDay, endOfDay } from "date-fns";
import type { CgmReading, LogEntry, PeriodTag } from "../types";
import { getQATotal, calculateIOB, PERIOD_TAG_INFO } from "../types";

// Pattern severity levels
export type PatternSeverity = "info" | "warning" | "action";

// Pattern types
export type PatternType =
  | "dawn"
  | "post-meal"
  | "hypo-pattern"
  | "hyper-pattern"
  | "exercise"
  | "sick"
  | "nighttime"
  | "ratio"
  | "basal"
  | "correction"
  | "tir"
  | "variability";

// Pattern result structure
export interface PatternResult {
  type: PatternType;
  severity: PatternSeverity;
  title: string;
  detail: string;
  dafneRecommendation: string;
  affectedPeriods?: PeriodTag[];
  confidence: number; // 0-1 based on data quality
  data?: {
    avgBg?: number;
    count?: number;
    times?: string[];
    tir?: number;
  };
}

// Prediction types
export type PredictionType =
  | "bg-trajectory"
  | "hypo-risk"
  | "hyper-risk"
  | "stacking"
  | "missing-long"
  | "exercise-effect"
  | "correction-effect"
  | "yesterday-compare"
  | "carb-effect"
  | "info";

// Prediction result structure
export type PredictionSource = "today-only" | "history-supported";

export interface PredictionHistorySupportContext {
  daysAnalyzed: number;
  daysWithDiaryData: number;
  usedDiaryAssessment: boolean;
}

export interface PredictionResult {
  type: PredictionType;
  timeframe: string;
  likelihood: "low" | "medium" | "high" | "info";
  basedOn: string;
  action?: string;
  currentIob?: number;
  predictedBg?: number;
  predictionSource?: PredictionSource;
  supportStrength?: "low" | "medium" | "high";
  supportNote?: string;
  /** Sort priority - lower numbers appear first */
  priority?: number;
  /** When this prediction became relevant (e.g., time of the triggering dose) */
  relevantFrom?: Date;
  /** When this prediction expires/becomes irrelevant */
  validUntil?: Date;
}

// Analysis options
export interface AnalysisOptions {
  cgmReadings: CgmReading[];
  entries: LogEntry[];
  daysToAnalyze?: number; // Default 7
  targetLow?: number; // Default 4
  targetHigh?: number; // Default 10
}

// Time period definitions for analysis
const TIME_PERIODS = {
  dawn: { start: 5, end: 8, label: "Dawn (5-8 AM)" },
  morning: { start: 8, end: 12, label: "Morning (8 AM-12 PM)" },
  afternoon: { start: 12, end: 17, label: "Afternoon (12-5 PM)" },
  evening: { start: 17, end: 21, label: "Evening (5-9 PM)" },
  night: { start: 21, end: 24, label: "Night (9 PM-12 AM)" },
  overnight: { start: 0, end: 5, label: "Overnight (12-5 AM)" },
};

// Post-meal windows (hours after meal period start)
const POST_MEAL_WINDOWS = {
  B: { peakStart: 1, peakEnd: 3, label: "Post-Breakfast" },
  L: { peakStart: 1, peakEnd: 3, label: "Post-Lunch" },
  EM: { peakStart: 1, peakEnd: 3, label: "Post-Evening Meal" },
};

/**
 * Get timestamp as number (handles Date, number, and ISO string)
 */
function getTimestamp(ts: Date | number | string): number {
  if (typeof ts === "number") return ts;
  if (typeof ts === "string") return new Date(ts).getTime();
  return ts.getTime();
}

/**
 * Filter readings by time period
 */
function filterByTimePeriod(
  readings: CgmReading[],
  startHour: number,
  endHour: number,
): CgmReading[] {
  return readings.filter((r) => {
    const hour = new Date(getTimestamp(r.timestamp)).getHours();
    if (startHour < endHour) {
      return hour >= startHour && hour < endHour;
    } else {
      // Handles overnight wrap (e.g., 21-5)
      return hour >= startHour || hour < endHour;
    }
  });
}

/**
 * Calculate average glucose from readings
 */
function calculateAvg(readings: CgmReading[]): number {
  if (readings.length === 0) return 0;
  return readings.reduce((sum, r) => sum + r.glucose, 0) / readings.length;
}

/**
 * Calculate standard deviation of glucose
 */
function calculateStdDev(readings: CgmReading[]): number {
  if (readings.length < 2) return 0;
  const avg = calculateAvg(readings);
  const squaredDiffs = readings.map((r) => Math.pow(r.glucose - avg, 2));
  return Math.sqrt(
    squaredDiffs.reduce((sum, d) => sum + d, 0) / readings.length,
  );
}

/**
 * Calculate Time in Range (TIR)
 */
function calculateTir(
  readings: CgmReading[],
  low: number,
  high: number,
): number {
  if (readings.length === 0) return 0;
  const inRange = readings.filter(
    (r) => r.glucose >= low && r.glucose <= high,
  ).length;
  return Math.round((inRange / readings.length) * 100);
}

/**
 * Count hypos (readings below threshold)
 */
function countHypos(readings: CgmReading[], threshold = 4): number {
  return readings.filter((r) => r.glucose < threshold).length;
}

/**
 * Count highs (readings above threshold)
 */
function countHighs(readings: CgmReading[], threshold = 10): number {
  return readings.filter((r) => r.glucose > threshold).length;
}

/**
 * Episode: A continuous period where readings are above/below a threshold
 */
interface Episode {
  startTime: number;
  endTime: number;
  readings: CgmReading[];
  avgGlucose: number;
  minGlucose: number;
  maxGlucose: number;
  durationMinutes: number;
}

/**
 * Group consecutive readings into episodes based on threshold
 * Gap tolerance: readings up to 30 minutes apart are considered the same episode
 * For hypos (direction="below"), single readings also count as episodes
 */
function groupIntoEpisodes(
  readings: CgmReading[],
  threshold: number,
  direction: "above" | "below",
): Episode[] {
  const sorted = [...readings].sort(
    (a, b) => getTimestamp(a.timestamp) - getTimestamp(b.timestamp),
  );

  const filtered = sorted.filter((r) =>
    direction === "above" ? r.glucose > threshold : r.glucose < threshold,
  );

  console.log(
    `[groupIntoEpisodes] threshold=${threshold}, direction=${direction}`,
  );
  console.log(
    `[groupIntoEpisodes] Total readings: ${readings.length}, matching threshold: ${filtered.length}`,
  );

  if (filtered.length === 0) {
    console.log(`[groupIntoEpisodes] No matching readings found`);
    return [];
  }

  // Debug: show first few matching readings
  console.log(`[groupIntoEpisodes] First 5 matching readings:`);
  for (let i = 0; i < Math.min(5, filtered.length); i++) {
    const r = filtered[i];
    console.log(
      `  ${new Date(getTimestamp(r.timestamp)).toISOString()} - ${r.glucose} mmol/L`,
    );
  }

  const episodes: Episode[] = [];
  let currentEpisode: CgmReading[] = [filtered[0]];
  const gapTolerance = 30 * 60 * 1000; // 30 minutes (2 CGM readings typically)

  for (let i = 1; i < filtered.length; i++) {
    const prevTime = getTimestamp(filtered[i - 1].timestamp);
    const currTime = getTimestamp(filtered[i].timestamp);

    if (currTime - prevTime <= gapTolerance) {
      currentEpisode.push(filtered[i]);
    } else {
      // Finalize current episode
      // For hypos, even single readings count (brief hypo is still a hypo)
      // For highs, require at least 2 readings (sustained high)
      const minReadings = direction === "below" ? 1 : 2;
      if (currentEpisode.length >= minReadings) {
        episodes.push(createEpisode(currentEpisode));
      }
      currentEpisode = [filtered[i]];
    }
  }

  // Don't forget the last episode
  const minReadings = direction === "below" ? 1 : 2;
  if (currentEpisode.length >= minReadings) {
    episodes.push(createEpisode(currentEpisode));
  }

  console.log(`[groupIntoEpisodes] Created ${episodes.length} episodes`);
  for (let i = 0; i < Math.min(5, episodes.length); i++) {
    const ep = episodes[i];
    console.log(
      `  Episode ${i + 1}: ${new Date(ep.startTime).toISOString()} - ${ep.readings.length} readings, ${ep.durationMinutes} min, avg=${ep.avgGlucose.toFixed(1)}`,
    );
  }
  if (episodes.length > 5) {
    console.log(`  ... and ${episodes.length - 5} more episodes`);
  }

  return episodes;
}

function createEpisode(readings: CgmReading[]): Episode {
  const sorted = readings.sort(
    (a, b) => getTimestamp(a.timestamp) - getTimestamp(b.timestamp),
  );
  const startTime = getTimestamp(sorted[0].timestamp);
  const endTime = getTimestamp(sorted[sorted.length - 1].timestamp);

  return {
    startTime,
    endTime,
    readings: sorted,
    avgGlucose: calculateAvg(sorted),
    minGlucose: Math.min(...sorted.map((r) => r.glucose)),
    maxGlucose: Math.max(...sorted.map((r) => r.glucose)),
    durationMinutes: Math.round((endTime - startTime) / (60 * 1000)),
  };
}

/**
 * Get days affected by episodes
 */
function getDaysWithEpisodes(episodes: Episode[]): Set<string> {
  const days = new Set<string>();
  for (const ep of episodes) {
    const date = new Date(ep.startTime);
    days.add(`${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`);
  }
  return days;
}

/**
 * Get the actual number of unique days in the readings data
 */
function getActualDaysInData(readings: CgmReading[]): number {
  const days = new Set<string>();
  for (const r of readings) {
    const date = new Date(getTimestamp(r.timestamp));
    days.add(`${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`);
  }
  return days.size;
}

/**
 * Find typical time range when episodes occur
 */
function findTypicalTimeRange(episodes: Episode[]): {
  startHour: number;
  endHour: number;
  label: string;
} | null {
  if (episodes.length === 0) return null;

  // Count which hours have the most episode starts
  const hourCounts: Map<number, number> = new Map();
  for (const ep of episodes) {
    const hour = new Date(ep.startTime).getHours();
    hourCounts.set(hour, (hourCounts.get(hour) || 0) + 1);
  }

  // Find peak hour and build a range around it
  let peakHour = 0;
  let peakCount = 0;
  for (const [hour, count] of hourCounts) {
    if (count > peakCount) {
      peakHour = hour;
      peakCount = count;
    }
  }

  // Expand to find contiguous range where episodes occur
  let startHour = peakHour;
  let endHour = peakHour;

  // Expand backwards
  for (let h = peakHour - 1; h >= 0; h--) {
    if ((hourCounts.get(h) || 0) > 0) {
      startHour = h;
    } else {
      break;
    }
  }

  // Expand forwards
  for (let h = peakHour + 1; h < 24; h++) {
    if ((hourCounts.get(h) || 0) > 0) {
      endHour = h;
    } else {
      break;
    }
  }

  const formatHr = (h: number) => {
    if (h === 0) return "12 AM";
    if (h < 12) return `${h} AM`;
    if (h === 12) return "12 PM";
    return `${h - 12} PM`;
  };

  return {
    startHour,
    endHour: endHour + 1, // Make it inclusive
    label:
      startHour === endHour
        ? `around ${formatHr(startHour)}`
        : `${formatHr(startHour)}-${formatHr(endHour + 1)}`,
  };
}

/**
 * Format duration for display
 */
function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
}

/**
 * Analyze dawn phenomenon
 */
function analyzeDawnPhenomenon(
  readings: CgmReading[],
  targetHigh: number,
): PatternResult | null {
  const dawnReadings = filterByTimePeriod(
    readings,
    TIME_PERIODS.dawn.start,
    TIME_PERIODS.dawn.end,
  );

  if (dawnReadings.length < 10) return null;

  const avgDawn = calculateAvg(dawnReadings);
  const dawnHighs = countHighs(dawnReadings, targetHigh);
  const dawnHighPercent = Math.round((dawnHighs / dawnReadings.length) * 100);

  // Compare to overnight readings
  const overnightReadings = filterByTimePeriod(
    readings,
    TIME_PERIODS.overnight.start,
    TIME_PERIODS.overnight.end,
  );
  const avgOvernight =
    overnightReadings.length > 0 ? calculateAvg(overnightReadings) : avgDawn;

  // Dawn phenomenon: rising BG in early morning, typically 2+ mmol/L rise
  const rise = avgDawn - avgOvernight;

  if (avgDawn > targetHigh && rise > 1.5) {
    const severity: PatternSeverity = rise > 3 ? "action" : "warning";
    return {
      type: "dawn",
      severity,
      title: "🌅 Dawn Phenomenon Detected",
      detail: `Average early morning BG is ${avgDawn.toFixed(1)} mmol/L (${dawnHighPercent}% above target). Rising ${rise.toFixed(1)} mmol/L from overnight.`,
      dafneRecommendation:
        rise > 3
          ? "Consider increasing evening basal insulin or adjusting timing. If on MDI, try taking long-acting dose at bedtime instead of dinner."
          : "Monitor pattern. May need basal adjustment if consistent over 3+ days.",
      affectedPeriods: ["B"],
      confidence: dawnReadings.length > 30 ? 0.9 : 0.7,
      data: { avgBg: avgDawn, count: dawnReadings.length },
    };
  }

  return null;
}

/**
 * Analyze post-meal patterns for a specific period
 */
function analyzePostMealPattern(
  readings: CgmReading[],
  entries: LogEntry[],
  period: "B" | "L" | "EM",
  targetHigh: number,
): PatternResult | null {
  const window = POST_MEAL_WINDOWS[period];
  const periodEntries = entries.filter((e) => e.periodTag === period);

  if (periodEntries.length < 3) return null;

  // Get post-meal readings for each entry
  let totalPostMealReadings: CgmReading[] = [];
  let spikeCount = 0;

  for (const entry of periodEntries) {
    const entryTime = getTimestamp(entry.timestamp);
    const peakStartTime = entryTime + window.peakStart * 60 * 60 * 1000;
    const peakEndTime = entryTime + window.peakEnd * 60 * 60 * 1000;

    const postMealReadings = readings.filter((r) => {
      const ts = getTimestamp(r.timestamp);
      return ts >= peakStartTime && ts <= peakEndTime;
    });

    totalPostMealReadings = [...totalPostMealReadings, ...postMealReadings];

    // Check if this meal caused a significant spike
    const avgPostMeal = calculateAvg(postMealReadings);
    if (avgPostMeal > targetHigh + 2) {
      spikeCount++;
    }
  }

  if (totalPostMealReadings.length < 5) return null;

  const avgPostMeal = calculateAvg(totalPostMealReadings);
  const spikePercent = Math.round((spikeCount / periodEntries.length) * 100);

  if (avgPostMeal > targetHigh + 1 && spikePercent > 50) {
    const periodName = PERIOD_TAG_INFO[period].label;
    return {
      type: "post-meal",
      severity: avgPostMeal > targetHigh + 3 ? "action" : "warning",
      title: `🍽️ ${window.label} Spikes`,
      detail: `${spikePercent}% of ${periodName.toLowerCase()} meals resulted in high BG. Average post-meal: ${avgPostMeal.toFixed(1)} mmol/L.`,
      dafneRecommendation: `Consider increasing the ${periodName.toLowerCase()} CP ratio (more units per CP). Also check carb counting accuracy for typical ${periodName.toLowerCase()} foods.`,
      affectedPeriods: [period],
      confidence: periodEntries.length > 5 ? 0.85 : 0.65,
      data: { avgBg: avgPostMeal, count: periodEntries.length },
    };
  }

  return null;
}

/**
 * Analyze hypoglycemia patterns using episode-based aggregation
 */
function analyzeHypoPatterns(
  readings: CgmReading[],
  daysAnalyzed: number,
): PatternResult | null {
  console.log(
    `[analyzeHypoPatterns] Starting with ${readings.length} readings, daysAnalyzed=${daysAnalyzed}`,
  );
  const hypoEpisodes = groupIntoEpisodes(readings, 4.0, "below");

  console.log(
    `[analyzeHypoPatterns] Found ${hypoEpisodes.length} hypo episodes`,
  );

  if (hypoEpisodes.length < 2) {
    console.log(
      `[analyzeHypoPatterns] Not enough episodes (<2), returning null`,
    );
    return null;
  }

  const daysAffected = getDaysWithEpisodes(hypoEpisodes);
  const avgDuration =
    hypoEpisodes.reduce((sum, ep) => sum + ep.durationMinutes, 0) /
    hypoEpisodes.length;
  const timeRange = findTypicalTimeRange(hypoEpisodes);

  // Determine which period is most affected
  let affectedPeriod: PeriodTag | undefined;
  if (timeRange) {
    const peakHour = timeRange.startHour;
    if (peakHour >= 0 && peakHour < 5) affectedPeriod = "3AM";
    else if (peakHour >= 5 && peakHour < 10) affectedPeriod = "B";
    else if (peakHour >= 10 && peakHour < 15) affectedPeriod = "L";
    else if (peakHour >= 15 && peakHour < 21) affectedPeriod = "EM";
    else affectedPeriod = "BT";
  }

  const severity: PatternSeverity =
    hypoEpisodes.length >= 5 || daysAffected.size >= 4 ? "action" : "warning";

  return {
    type: "hypo-pattern",
    severity,
    title: "⚠️ Recurring Hypoglycemia",
    detail: `${hypoEpisodes.length} hypo episodes across ${daysAffected.size} of ${daysAnalyzed} days. Avg duration: ${formatDuration(Math.round(avgDuration))}. Typically occurs ${timeRange?.label || "throughout the day"}.`,
    dafneRecommendation:
      affectedPeriod === "3AM"
        ? "Nighttime lows suggest basal insulin may be too high. Consider reducing evening long-acting dose by 10-20%."
        : `Consider reducing quick-acting insulin at ${affectedPeriod ? PERIOD_TAG_INFO[affectedPeriod].label.toLowerCase() : "affected meals"} by 1-2 units, or review carb counting.`,
    affectedPeriods: affectedPeriod ? [affectedPeriod] : undefined,
    confidence: 0.8,
    data: { count: hypoEpisodes.length },
  };
}

/**
 * Analyze hyperglycemia patterns using episode-based aggregation
 */
function analyzeHyperPatterns(
  readings: CgmReading[],
  targetHigh: number,
  daysAnalyzed: number,
): PatternResult | null {
  // Group into episodes of significant highs (>14 mmol/L is concerning)
  const highThreshold = targetHigh + 4;
  const hyperEpisodes = groupIntoEpisodes(readings, highThreshold, "above");

  if (hyperEpisodes.length < 2) return null;

  const daysAffected = getDaysWithEpisodes(hyperEpisodes);
  const avgDuration =
    hyperEpisodes.reduce((sum, ep) => sum + ep.durationMinutes, 0) /
    hyperEpisodes.length;
  const avgGlucose =
    hyperEpisodes.reduce((sum, ep) => sum + ep.avgGlucose, 0) /
    hyperEpisodes.length;
  const timeRange = findTypicalTimeRange(hyperEpisodes);

  // Determine which period is most affected
  let affectedPeriod: PeriodTag | undefined;
  if (timeRange) {
    const peakHour = timeRange.startHour;
    if (peakHour >= 0 && peakHour < 5) affectedPeriod = "3AM";
    else if (peakHour >= 5 && peakHour < 10) affectedPeriod = "B";
    else if (peakHour >= 10 && peakHour < 15) affectedPeriod = "L";
    else if (peakHour >= 15 && peakHour < 21) affectedPeriod = "EM";
    else affectedPeriod = "BT";
  }

  const totalTimeHigh = hyperEpisodes.reduce(
    (sum, ep) => sum + ep.durationMinutes,
    0,
  );
  const severity: PatternSeverity =
    totalTimeHigh > 300 || daysAffected.size >= 5 ? "action" : "warning"; // >5 hours total high time

  return {
    type: "hyper-pattern",
    severity,
    title: "📈 Persistent High Readings",
    detail: `${hyperEpisodes.length} extended high episodes (>${highThreshold} mmol/L) across ${daysAffected.size} of ${daysAnalyzed} days. Avg duration: ${formatDuration(Math.round(avgDuration))}. Avg BG during episodes: ${avgGlucose.toFixed(1)} mmol/L. Typically occurs ${timeRange?.label || "throughout the day"}.`,
    dafneRecommendation: `Review ${affectedPeriod ? PERIOD_TAG_INFO[affectedPeriod].label.toLowerCase() : "meal"} dosing. May need tighter ratio or increased correction factor sensitivity.`,
    affectedPeriods: affectedPeriod ? [affectedPeriod] : undefined,
    confidence: 0.75,
    data: { avgBg: avgGlucose, count: hyperEpisodes.length },
  };
}

/**
 * Analyze exercise impact
 */
function analyzeExerciseImpact(
  readings: CgmReading[],
  entries: LogEntry[],
): PatternResult | null {
  const exerciseEntries = entries.filter((e) => e.exercise);

  if (exerciseEntries.length < 2) return null;

  let postExerciseLows = 0;

  for (const entry of exerciseEntries) {
    const entryTime = getTimestamp(entry.timestamp);
    // Check 2-8 hours post-exercise window
    const postStart = entryTime + 2 * 60 * 60 * 1000;
    const postEnd = entryTime + 8 * 60 * 60 * 1000;

    const postExReadings = readings.filter((r) => {
      const ts = getTimestamp(r.timestamp);
      return ts >= postStart && ts <= postEnd;
    });

    const lowReadings = postExReadings.filter((r) => r.glucose < 4.5);
    if (lowReadings.length > 0) {
      postExerciseLows++;
    }
  }

  const lowPercent = Math.round(
    (postExerciseLows / exerciseEntries.length) * 100,
  );

  if (lowPercent > 30) {
    return {
      type: "exercise",
      severity: "action",
      title: "🏃 Post-Exercise Lows",
      detail: `${lowPercent}% of exercise sessions (${postExerciseLows}/${exerciseEntries.length}) resulted in low BG within 2-8 hours.`,
      dafneRecommendation:
        "Consider reducing quick-acting insulin by 20-30% for meals after exercise, or have a small snack (10-15g carbs) without bolus post-exercise.",
      confidence: exerciseEntries.length > 4 ? 0.85 : 0.65,
      data: { count: exerciseEntries.length },
    };
  }

  return null;
}

/**
 * Analyze sick day patterns
 */
function analyzeSickDayPatterns(
  readings: CgmReading[],
  entries: LogEntry[],
): PatternResult | null {
  const sickEntries = entries.filter((e) => e.sickDay?.status === "sick");

  if (sickEntries.length === 0) return null;

  // Get readings during sick period
  let sickReadings: CgmReading[] = [];

  for (const entry of sickEntries) {
    const entryTime = getTimestamp(entry.timestamp);
    const dayStart = startOfDay(new Date(entryTime)).getTime();
    const dayEnd = endOfDay(new Date(entryTime)).getTime();

    const dayReadings = readings.filter((r) => {
      const ts = getTimestamp(r.timestamp);
      return ts >= dayStart && ts <= dayEnd;
    });

    sickReadings = [...sickReadings, ...dayReadings];
  }

  if (sickReadings.length < 10) return null;

  const avgSick = calculateAvg(sickReadings);

  return {
    type: "sick",
    severity: "action",
    title: "🤒 Sick Day Management Active",
    detail: `Sick day entries detected. Average BG during illness: ${avgSick.toFixed(1)} mmol/L. BG typically runs higher when unwell.`,
    dafneRecommendation:
      "Continue 2-hourly BG monitoring. Check ketones regularly. May need 10-20% extra on correction doses. Never skip basal insulin even if not eating.",
    confidence: 0.9,
    data: { avgBg: avgSick, count: sickEntries.length },
  };
}

/**
 * Analyze nighttime patterns (for basal assessment)
 */
function analyzeNighttimePatterns(
  readings: CgmReading[],
  _targetLow: number,
  targetHigh: number,
  daysAnalyzed: number,
): PatternResult | null {
  // Check overnight readings (midnight to 5am) when basal should be primary driver
  const overnightReadings = filterByTimePeriod(
    readings,
    TIME_PERIODS.overnight.start,
    TIME_PERIODS.overnight.end,
  );

  if (overnightReadings.length < 20) return null;

  const avgOvernight = calculateAvg(overnightReadings);
  const stdDev = calculateStdDev(overnightReadings);

  // Use episode-based analysis for nighttime lows
  const nightHypoEpisodes = groupIntoEpisodes(overnightReadings, 4.0, "below");
  const daysWithNightHypos = getDaysWithEpisodes(nightHypoEpisodes);

  // Nighttime hypos - basal too high
  if (nightHypoEpisodes.length >= 2 || daysWithNightHypos.size >= 2) {
    const avgDuration =
      nightHypoEpisodes.length > 0
        ? nightHypoEpisodes.reduce((sum, ep) => sum + ep.durationMinutes, 0) /
          nightHypoEpisodes.length
        : 0;

    return {
      type: "nighttime",
      severity: "action",
      title: "🌙 Nighttime Hypoglycemia Risk",
      detail: `${nightHypoEpisodes.length} overnight low episodes across ${daysWithNightHypos.size} of ${daysAnalyzed} nights${avgDuration > 0 ? `. Avg duration: ${formatDuration(Math.round(avgDuration))}` : ""}. This suggests basal insulin may be too high.`,
      dafneRecommendation:
        "Consider reducing evening basal insulin by 10-20%. If on twice-daily basal, focus on evening dose.",
      confidence: 0.85,
      data: { avgBg: avgOvernight, count: nightHypoEpisodes.length },
    };
  }

  // Check for consistent overnight highs using percentage of time above target
  const highReadings = overnightReadings.filter((r) => r.glucose > targetHigh);
  const highPercent = Math.round(
    (highReadings.length / overnightReadings.length) * 100,
  );

  // Consistent overnight highs - basal too low
  if (highPercent > 40 && avgOvernight > targetHigh) {
    return {
      type: "basal",
      severity: "warning",
      title: "📊 Basal May Need Increase",
      detail: `Average overnight BG is ${avgOvernight.toFixed(1)} mmol/L with ${highPercent}% of time above target. Stable overnight highs suggest insufficient basal.`,
      dafneRecommendation:
        "Consider increasing basal insulin by 1-2 units. Wait 2-3 days to see effect before further changes.",
      affectedPeriods: ["BT"],
      confidence: stdDev < 2 ? 0.85 : 0.65, // Higher confidence if readings are stable
      data: { avgBg: avgOvernight },
    };
  }

  return null;
}

/**
 * Analyze overall time in range
 */
function analyzeTir(
  readings: CgmReading[],
  targetLow: number,
  targetHigh: number,
): PatternResult | null {
  if (readings.length < 50) return null;

  const tir = calculateTir(readings, targetLow, targetHigh);
  const hypoPercent = Math.round(
    (countHypos(readings, targetLow) / readings.length) * 100,
  );
  const highPercent = Math.round(
    (countHighs(readings, targetHigh) / readings.length) * 100,
  );

  if (tir >= 70) {
    return {
      type: "tir",
      severity: "info",
      title: "✅ Good Time in Range",
      detail: `${tir}% of readings within target (${targetLow}-${targetHigh} mmol/L). ${hypoPercent}% low, ${highPercent}% high.`,
      dafneRecommendation:
        "Current insulin doses appear well-balanced. Continue monitoring for patterns and adjust as needed.",
      confidence: 0.95,
      data: { tir },
    };
  } else if (tir < 50) {
    return {
      type: "tir",
      severity: "action",
      title: "⚠️ Low Time in Range",
      detail: `Only ${tir}% of readings within target. ${hypoPercent}% low, ${highPercent}% high.`,
      dafneRecommendation:
        hypoPercent > highPercent
          ? "Focus on preventing lows first - consider reducing doses. Once lows are controlled, address highs."
          : "Review carb counting accuracy and consider tightening ratios. May also need basal adjustment.",
      confidence: 0.9,
      data: { tir },
    };
  }

  return null;
}

/**
 * Analyze glucose variability
 */
function analyzeVariability(readings: CgmReading[]): PatternResult | null {
  if (readings.length < 50) return null;

  const stdDev = calculateStdDev(readings);
  const cv = (stdDev / calculateAvg(readings)) * 100; // Coefficient of variation

  if (cv > 36) {
    // High variability threshold
    return {
      type: "variability",
      severity: "warning",
      title: "📉 High Glucose Variability",
      detail: `Glucose coefficient of variation is ${cv.toFixed(1)}% (target <36%). Large swings between highs and lows.`,
      dafneRecommendation:
        "High variability often indicates inconsistent carb counting or meal timing. Focus on eating similar portion sizes and consistent meal times.",
      confidence: 0.8,
    };
  }

  return null;
}

/**
 * Main pattern analysis function
 */
export function analyzePatterns(options: AnalysisOptions): PatternResult[] {
  const {
    cgmReadings,
    entries,
    daysToAnalyze = 7,
    targetLow = 4,
    targetHigh = 10,
  } = options;

  console.log("[PatternAnalysis] Starting analysis");
  console.log(
    `[PatternAnalysis] Input: ${cgmReadings.length} CGM readings, ${entries.length} entries, daysToAnalyze=${daysToAnalyze}`,
  );

  const patterns: PatternResult[] = [];

  if (cgmReadings.length < 20) {
    console.log("[PatternAnalysis] Not enough data (<20 readings)");
    return patterns; // Not enough data
  }

  // Find the date range of actual data (use data end, not "now")
  let maxTs = -Infinity;
  let minTs = Infinity;
  const now = Date.now();
  let maxReading: CgmReading | null = null;
  let futureCount = 0;

  for (const r of cgmReadings) {
    const ts = getTimestamp(r.timestamp);
    if (ts > maxTs) {
      maxTs = ts;
      maxReading = r;
    }
    if (ts < minTs) minTs = ts;
    if (ts > now) futureCount++;
  }

  console.log(
    `[PatternAnalysis] Raw data range: ${new Date(minTs).toISOString()} to ${new Date(maxTs).toISOString()}`,
  );
  console.log(`[PatternAnalysis] Current time: ${new Date(now).toISOString()}`);
  console.log(`[PatternAnalysis] Future readings count: ${futureCount}`);
  if (maxReading) {
    console.log(`[PatternAnalysis] Max reading details:`, {
      id: maxReading.id,
      timestamp: maxReading.timestamp,
      timestampType: typeof maxReading.timestamp,
      glucose: maxReading.glucose,
      source: maxReading.source,
    });
  }

  // Cap the end date to "now" if data contains future timestamps
  if (maxTs > now) {
    console.log(`[PatternAnalysis] Data contains future dates, capping to now`);
    maxTs = now;
  }

  // Filter to the most recent N days of actual data
  const dataEndDate = new Date(maxTs);
  const cutoff = subDays(dataEndDate, daysToAnalyze);
  console.log(
    `[PatternAnalysis] Data end: ${dataEndDate.toISOString()}, cutoff: ${cutoff.toISOString()}`,
  );

  const filteredReadings = cgmReadings.filter(
    (r) => getTimestamp(r.timestamp) >= cutoff.getTime(),
  );

  const filteredEntries = entries.filter(
    (e) => getTimestamp(e.timestamp) >= cutoff.getTime(),
  );

  console.log(
    `[PatternAnalysis] After filtering: ${filteredReadings.length} readings, ${filteredEntries.length} entries`,
  );

  if (filteredReadings.length < 20) {
    console.log("[PatternAnalysis] Not enough filtered data (<20 readings)");
    return patterns; // Not enough data after filtering
  }

  // Calculate actual days span in filtered data
  const actualDays = getActualDaysInData(filteredReadings);
  console.log(`[PatternAnalysis] Actual days in filtered data: ${actualDays}`);

  // Debug: count hypo readings
  const hypoCount = filteredReadings.filter(
    (r) => r.glucose < targetLow,
  ).length;
  console.log(`[PatternAnalysis] Hypo readings (<${targetLow}): ${hypoCount}`);

  // Run all pattern analyses
  const dawnPattern = analyzeDawnPhenomenon(filteredReadings, targetHigh);
  if (dawnPattern) patterns.push(dawnPattern);

  // Post-meal patterns for each main meal
  for (const period of ["B", "L", "EM"] as const) {
    const mealPattern = analyzePostMealPattern(
      filteredReadings,
      filteredEntries,
      period,
      targetHigh,
    );
    if (mealPattern) patterns.push(mealPattern);
  }

  const hypoPattern = analyzeHypoPatterns(filteredReadings, actualDays);
  if (hypoPattern) patterns.push(hypoPattern);

  const hyperPattern = analyzeHyperPatterns(
    filteredReadings,
    targetHigh,
    actualDays,
  );
  if (hyperPattern) patterns.push(hyperPattern);

  const exercisePattern = analyzeExerciseImpact(
    filteredReadings,
    filteredEntries,
  );
  if (exercisePattern) patterns.push(exercisePattern);

  const sickPattern = analyzeSickDayPatterns(filteredReadings, filteredEntries);
  if (sickPattern) patterns.push(sickPattern);

  const nightPattern = analyzeNighttimePatterns(
    filteredReadings,
    targetLow,
    targetHigh,
    actualDays,
  );
  if (nightPattern) patterns.push(nightPattern);

  const tirPattern = analyzeTir(filteredReadings, targetLow, targetHigh);
  if (tirPattern) patterns.push(tirPattern);

  const variabilityPattern = analyzeVariability(filteredReadings);
  if (variabilityPattern) patterns.push(variabilityPattern);

  // Sort by severity (action first, then warning, then info)
  const severityOrder: Record<PatternSeverity, number> = {
    action: 0,
    warning: 1,
    info: 2,
  };

  patterns.sort(
    (a, b) => severityOrder[a.severity] - severityOrder[b.severity],
  );

  return patterns;
}

/**
 * Generate predictions based on current state and historical patterns.
 * Works with diary data alone OR diary + CGM data.
 *
 * DAFNE-FIRST APPROACH: CGM may sync daily/weekly, so predictions prioritise
 * DAFNE rule-based modelling (carb ratios, insulin timing, absorption curves)
 * for forward guidance. CGM primarily supports historical analysis.
 */
export function generatePredictions(
  currentEntries: LogEntry[], // Today's entries
  cgmReadings: CgmReading[], // Recent readings (may be empty)
  historicalPatterns: PatternResult[],
  currentPeriod: PeriodTag,
  yesterdayEntries?: LogEntry[], // Optional: yesterday's entries for comparison
  historySupport?: PredictionHistorySupportContext,
  correctionFactor: number = 2.5, // How much 1u insulin drops GL (default OZDAFNE midpoint)
): PredictionResult[] {
  const predictions: PredictionResult[] = [];
  const now = new Date();
  const currentHour = now.getHours();
  const historySupportStrength: "low" | "medium" | "high" = historySupport
    ? historySupport.daysWithDiaryData >= 5
      ? "high"
      : historySupport.daysWithDiaryData >= 3
        ? "medium"
        : "low"
    : "low";
  const historySupportNote = historySupport
    ? historySupport.usedDiaryAssessment
      ? `History-supported: ${historySupport.daysWithDiaryData}/${historySupport.daysAnalyzed} days with diary data.`
      : `History fallback: only ${historySupport.daysWithDiaryData}/${historySupport.daysAnalyzed} days with diary data.`
    : "History-supported by recent data.";

  // ============================================
  // DETECT ACTIVE MEAL STATE
  // A "meal" is insulin logged with carbs within 30 mins
  // During meal absorption, we model rise-and-flatten, not just insulin drop
  // ============================================
  const activeMeals = currentEntries.filter((e) => {
    const hoursAgo =
      (now.getTime() - getTimestamp(e.timestamp)) / (1000 * 60 * 60);
    const hasCarbs = (e.carbs ?? 0) > 0;
    const hasQA = (getQATotal(e) ?? 0) > 0;
    // Meal state: carbs + QA within absorption window (0-3 hours)
    return hoursAgo >= 0 && hoursAgo <= 3 && hasCarbs && hasQA;
  });

  const isInMealState = activeMeals.length > 0;
  const latestMeal = activeMeals.sort(
    (a, b) => getTimestamp(b.timestamp) - getTimestamp(a.timestamp),
  )[0];

  // Calculate MEAL insulin (QA logged with carbs) vs CORRECTION insulin (QA alone)
  let mealIob = 0;
  let correctionIob = 0;
  for (const entry of currentEntries) {
    const qaTotal = getQATotal(entry);
    if (qaTotal) {
      const hoursElapsed =
        (now.getTime() - getTimestamp(entry.timestamp)) / (1000 * 60 * 60);
      if (hoursElapsed >= 0 && hoursElapsed <= 5) {
        const iob = calculateIOB(qaTotal, hoursElapsed);
        // If entry has carbs, it's meal insulin; otherwise correction
        if (entry.carbs && entry.carbs > 0) {
          mealIob += iob;
        } else {
          correctionIob += iob;
        }
      }
    }
  }
  mealIob = Math.round(mealIob * 10) / 10;
  correctionIob = Math.round(correctionIob * 10) / 10;
  const currentIob = Math.round((mealIob + correctionIob) * 10) / 10;

  // Get latest BG from CGM (if available)
  const recentReadings = cgmReadings
    .filter((r) => {
      const age = (now.getTime() - getTimestamp(r.timestamp)) / (1000 * 60);
      return age <= 30; // Last 30 minutes
    })
    .sort((a, b) => getTimestamp(b.timestamp) - getTimestamp(a.timestamp));

  const latestCgmBg =
    recentReadings.length > 0 ? recentReadings[0].glucose : null;

  // Get latest logged GL from diary entries
  const entriesWithGL = currentEntries
    .filter((e) => e.bloodGlucose !== undefined)
    .sort((a, b) => getTimestamp(b.timestamp) - getTimestamp(a.timestamp));
  const latestLoggedGL = entriesWithGL.length > 0 ? entriesWithGL[0] : null;

  // Use CGM if available, else diary GL
  const latestBg = latestCgmBg ?? latestLoggedGL?.bloodGlucose;

  // ============================================
  // DIARY-BASED PREDICTIONS (work without CGM)
  // ============================================

  // 1. Missing Long Insulin Warning
  const hasLongToday = currentEntries.some(
    (e) =>
      (e.biBase !== undefined && e.biBase > 0) ||
      (e.biCorrection !== undefined && e.biCorrection > 0),
  );
  if (!hasLongToday && currentHour >= 20) {
    predictions.push({
      type: "missing-long",
      timeframe: "before bed",
      likelihood: "high",
      basedOn: "No long-acting insulin recorded today",
      action: "Remember to take your long-acting insulin before bed",
      priority: 1,
    });
  }

  // 2. Yesterday Comparison (if yesterday data available)
  if (yesterdayEntries && yesterdayEntries.length > 0) {
    // Find yesterday's entry closest to current time
    const targetHour = currentHour;
    const yesterdayAtSameTime = yesterdayEntries
      .filter((e) => {
        const entryHour = new Date(getTimestamp(e.timestamp)).getHours();
        return Math.abs(entryHour - targetHour) <= 1; // Within 1 hour
      })
      .sort((a, b) => {
        const aHour = new Date(getTimestamp(a.timestamp)).getHours();
        const bHour = new Date(getTimestamp(b.timestamp)).getHours();
        return Math.abs(aHour - targetHour) - Math.abs(bHour - targetHour);
      })[0];

    if (yesterdayAtSameTime?.bloodGlucose) {
      const yesterdayGL = yesterdayAtSameTime.bloodGlucose;
      const currentGL = latestBg;

      if (currentGL) {
        const diff = currentGL - yesterdayGL;
        const trend = diff > 1 ? "higher" : diff < -1 ? "lower" : "similar";
        predictions.push({
          type: "yesterday-compare",
          timeframe: "comparison",
          likelihood: "info",
          basedOn: `This time yesterday: ${yesterdayGL.toFixed(1)} mmol/L. Today: ${currentGL.toFixed(1)} (${trend})`,
          predictionSource: "history-supported",
          supportStrength: "low",
          supportNote: "One-off comparison with yesterday at similar time.",
          priority: 10,
        });
      } else {
        predictions.push({
          type: "yesterday-compare",
          timeframe: "comparison",
          likelihood: "info",
          basedOn: `This time yesterday your GL was ${yesterdayGL.toFixed(1)} mmol/L`,
          predictionSource: "history-supported",
          supportStrength: "low",
          supportNote: "One-off comparison with yesterday at similar time.",
          priority: 10,
        });
      }
    }
  }

  // 3. Correction Effect Estimate
  const recentCorrections = currentEntries.filter((e) => {
    const hoursAgo =
      (now.getTime() - getTimestamp(e.timestamp)) / (1000 * 60 * 60);
    return hoursAgo <= 3 && e.qaCorrection && e.qaCorrection > 0;
  });

  if (recentCorrections.length > 0) {
    const lastCorrection = recentCorrections.sort(
      (a, b) => getTimestamp(b.timestamp) - getTimestamp(a.timestamp),
    )[0];
    const corrUnits = lastCorrection.qaCorrection || 0;
    const hoursAgo =
      (now.getTime() - getTimestamp(lastCorrection.timestamp)) /
      (1000 * 60 * 60);

    // DAFNE: 1 unit drops GL by user's correction factor
    const expectedDrop = corrUnits * correctionFactor;
    const remainingEffect = expectedDrop * calculateIOB(1, hoursAgo);
    const correctionTime = new Date(getTimestamp(lastCorrection.timestamp));
    // Correction effect expires ~4 hours after dose
    const effectExpiry = new Date(
      correctionTime.getTime() + 4 * 60 * 60 * 1000,
    );

    if (remainingEffect > 0.5) {
      predictions.push({
        type: "correction-effect",
        timeframe: `next ${Math.round((4 - hoursAgo) * 60)} mins`,
        likelihood: "info",
        basedOn: `${corrUnits}u correction taken ${Math.round(hoursAgo * 60)} mins ago`,
        action: `Expect GL to drop ~${remainingEffect.toFixed(1)} mmol/L more`,
        currentIob,
        priority: 3,
        relevantFrom: correctionTime,
        validUntil: effectExpiry,
      });
    }
  }

  // 4. Recent Carbs Effect (only show for UNCOVERED carbs - covered carbs handled in meal state)
  const recentUncoveredCarbs = currentEntries.filter((e) => {
    const hoursAgo =
      (now.getTime() - getTimestamp(e.timestamp)) / (1000 * 60 * 60);
    const hasCarbs = (e.carbs ?? 0) > 0;
    const hasQA = (getQATotal(e) ?? 0) > 0;
    // Only show warning for carbs WITHOUT insulin coverage
    return hoursAgo <= 2 && hoursAgo >= 0 && hasCarbs && !hasQA;
  });

  if (recentUncoveredCarbs.length > 0) {
    const lastCarb = recentUncoveredCarbs.sort(
      (a, b) => getTimestamp(b.timestamp) - getTimestamp(a.timestamp),
    )[0];
    const carbsGrams = lastCarb.carbs || 0;
    const carbPortions = carbsGrams / 10;
    const hoursAgo =
      (now.getTime() - getTimestamp(lastCarb.timestamp)) / (1000 * 60 * 60);

    // DAFNE: 1 CP raises GL by 2-3 mmol/L, peaks at 30-90 mins
    if (hoursAgo < 1.5) {
      const expectedRise = carbPortions * 2.5;
      const peakExpected = hoursAgo < 0.5;
      const carbTime = new Date(getTimestamp(lastCarb.timestamp));
      // Carb effect expires ~2-3 hours after eating
      const carbExpiry = new Date(carbTime.getTime() + 3 * 60 * 60 * 1000);

      predictions.push({
        type: "carb-effect",
        timeframe: peakExpected ? "next 30-60 mins" : "ongoing",
        likelihood: expectedRise > 5 ? "medium" : "info",
        basedOn: `${carbsGrams}g uncovered carbs (${carbPortions.toFixed(1)} CP) - no insulin logged`,
        action: peakExpected
          ? `Expect GL rise ~${expectedRise.toFixed(1)} mmol/L - consider if insulin needed`
          : "Carb absorption in progress without insulin coverage",
        priority: peakExpected ? 2 : 4,
        relevantFrom: carbTime,
        validUntil: carbExpiry,
      });
    }
  }

  // 5. Exercise Effect (check for recent exercise)
  const recentExercise = currentEntries.filter((e) => {
    const hoursAgo =
      (now.getTime() - getTimestamp(e.timestamp)) / (1000 * 60 * 60);
    return hoursAgo <= 12 && e.exercise;
  });

  if (recentExercise.length > 0) {
    const lastExercise = recentExercise.sort(
      (a, b) => getTimestamp(b.timestamp) - getTimestamp(a.timestamp),
    )[0];
    const hoursAgo =
      (now.getTime() - getTimestamp(lastExercise.timestamp)) / (1000 * 60 * 60);
    const intensity = lastExercise.exercise?.intensity || "moderate";

    // Exercise effects can last up to 24-48 hours
    const exerciseTime = new Date(getTimestamp(lastExercise.timestamp));
    // Exercise effect validity varies by intensity
    const exerciseValidHours = intensity === "vigorous" ? 12 : 6;
    const exerciseExpiry = new Date(
      exerciseTime.getTime() + exerciseValidHours * 60 * 60 * 1000,
    );

    if (hoursAgo <= 6) {
      predictions.push({
        type: "exercise-effect",
        timeframe: `next ${Math.round(6 - hoursAgo)} hours`,
        likelihood: intensity === "vigorous" ? "medium" : "low",
        basedOn: `${lastExercise.exercise?.type || "Exercise"} (${intensity}) ${Math.round(hoursAgo)} hours ago`,
        action:
          "Increased insulin sensitivity - consider reducing doses by 10-20%",
        priority: 5,
        relevantFrom: exerciseTime,
        validUntil: exerciseExpiry,
      });
    } else if (hoursAgo <= 12) {
      predictions.push({
        type: "exercise-effect",
        timeframe: "overnight/next day",
        likelihood: "low",
        basedOn: `Exercise effect from ${Math.round(hoursAgo)} hours ago may still affect sensitivity`,
        action: "Monitor for delayed hypos, especially overnight",
        priority: 8,
        relevantFrom: exerciseTime,
        validUntil: exerciseExpiry,
      });
    }
  }

  // 6. GL Trajectory Prediction with DAFNE Meal Modelling
  // DAFNE Rule: 1 CP (10g carbs) raises GL ~2.5 mmol/L, 1u insulin drops GL ~2.5 mmol/L
  // In meal state: model rise-and-flatten, don't just predict drop
  if (latestLoggedGL && latestLoggedGL.bloodGlucose) {
    const glHoursAgo =
      (now.getTime() - getTimestamp(latestLoggedGL.timestamp)) /
      (1000 * 60 * 60);

    if (glHoursAgo <= 2) {
      if (isInMealState && latestMeal) {
        // MEAL STATE: Model combined carb rise + insulin flatten effect
        const mealHoursAgo =
          (now.getTime() - getTimestamp(latestMeal.timestamp)) /
          (1000 * 60 * 60);
        const carbsGrams = latestMeal.carbs || 0;
        const carbPortions = carbsGrams / 10;
        const mealQA = getQATotal(latestMeal);

        // DAFNE absorption curves:
        // Carbs: peak at 30-90 mins, mostly absorbed by 2-3 hours
        // Insulin: peak at 1-2 hours, active for 4-5 hours

        // Remaining carb effect (carbs absorb faster than insulin works)
        let remainingCarbRise = 0;
        if (mealHoursAgo < 2) {
          // Linear decay of carb effect over 2 hours
          remainingCarbRise =
            carbPortions * 2.5 * Math.max(0, (2 - mealHoursAgo) / 2);
        }

        // Remaining insulin effect (IOB)
        const remainingInsulinDrop = mealIob * correctionFactor;

        // Net effect: carb rise minus insulin drop
        const netEffect = remainingCarbRise - remainingInsulinDrop;
        const predictedGL = Math.max(
          2,
          latestLoggedGL.bloodGlucose + netEffect,
        );

        // Only warn about hypo if net effect is significantly negative AND correction IOB exists
        if (predictedGL < 4 && correctionIob > 0.5) {
          predictions.push({
            type: "hypo-risk",
            timeframe: "next 2-3 hours",
            likelihood: predictedGL < 3 ? "high" : "medium",
            basedOn: `GL ${latestLoggedGL.bloodGlucose.toFixed(1)} with ${correctionIob}u correction IOB (meal separately covered)`,
            action: `Predicted GL: ${predictedGL.toFixed(1)} - correction may need reducing`,
            currentIob,
            predictedBg: predictedGL,
            priority: 0,
          });
        } else if (predictedGL >= 4 && predictedGL <= 8 && mealHoursAgo < 1.5) {
          // Good meal coverage - show positive feedback
          predictions.push({
            type: "info",
            timeframe: "meal absorption",
            likelihood: "info",
            basedOn: `Meal (${carbsGrams}g / ${mealQA}u) absorbing - GL expected to stay stable`,
            action: "Carb rise and insulin effect balancing as expected",
            priority: 6,
          });
        }
      } else if (correctionIob > 0) {
        // NO MEAL STATE: Pure correction - only consider correction IOB for hypo risk
        const predictedDrop = correctionIob * correctionFactor;
        const predictedGL = Math.max(
          2,
          latestLoggedGL.bloodGlucose - predictedDrop,
        );

        if (predictedGL < 4) {
          predictions.push({
            type: "hypo-risk",
            timeframe: "next 2-3 hours",
            likelihood: predictedGL < 3 ? "high" : "medium",
            basedOn: `GL ${latestLoggedGL.bloodGlucose.toFixed(1)} with ${correctionIob}u correction IOB active`,
            action: `Predicted GL: ${predictedGL.toFixed(1)} - consider having a snack`,
            currentIob: correctionIob,
            predictedBg: predictedGL,
            priority: 0,
          });
        }
      }
    }
  }

  // ============================================
  // PATTERN-BASED PREDICTIONS (need historical data)
  // ============================================

  // Stacking warning - only show when stacking HAS occurred (2+ correction doses with overlapping IOB)
  // The add-entry page handles WILL stack warnings; today page warns about stacking that already happened
  const correctionDosesWithActiveIOB = currentEntries.filter((e) => {
    const qaTotal = getQATotal(e) ?? 0;
    const hasCarbs = (e.carbs ?? 0) > 0;
    if (qaTotal <= 0 || hasCarbs) return false; // Only pure corrections
    const hoursAgo =
      (now.getTime() - getTimestamp(e.timestamp)) / (1000 * 60 * 60);
    return hoursAgo >= 0 && hoursAgo <= 4; // Within IOB window
  });

  if (correctionDosesWithActiveIOB.length >= 2 && correctionIob > 2) {
    // Stacking HAS occurred - multiple correction doses with overlapping IOB
    const totalCorrUnits = correctionDosesWithActiveIOB.reduce(
      (sum, e) => sum + (getQATotal(e) ?? 0),
      0,
    );
    // Find earliest and latest correction dose for time context
    const sortedDoses = correctionDosesWithActiveIOB.sort(
      (a, b) => getTimestamp(a.timestamp) - getTimestamp(b.timestamp),
    );
    const firstDoseTime = new Date(getTimestamp(sortedDoses[0].timestamp));
    const lastDoseTime = new Date(
      getTimestamp(sortedDoses[sortedDoses.length - 1].timestamp),
    );
    // IOB expires ~4 hours after last dose
    const validUntil = new Date(lastDoseTime.getTime() + 4 * 60 * 60 * 1000);

    predictions.push({
      type: "stacking",
      timeframe: "next 2 hours",
      likelihood: correctionIob > 4 ? "high" : "medium",
      basedOn: `${correctionDosesWithActiveIOB.length} correction doses (${totalCorrUnits}u total) taken within 4 hours`,
      action: "Insulin stacking detected - monitor closely for hypo symptoms",
      currentIob: correctionIob,
      priority: 2,
      relevantFrom: firstDoseTime,
      validUntil,
    });
  }

  // Hypo risk based on patterns (suppress during active meal state)
  const hypoPattern = historicalPatterns.find((p) => p.type === "hypo-pattern");
  const hasImmediateHypoRisk = predictions.some(
    (p) => p.type === "hypo-risk" && p.predictionSource !== "history-supported",
  );
  if (
    hypoPattern &&
    latestBg &&
    latestBg < 6 &&
    !isInMealState &&
    !hasImmediateHypoRisk
  ) {
    predictions.push({
      type: "hypo-risk",
      timeframe: "next 1-2 hours",
      likelihood: latestBg < 5 ? "high" : "medium",
      basedOn: `Current GL ${latestBg?.toFixed(1)} with historical hypo pattern. ${correctionIob > 0 ? `${correctionIob}u correction IOB active.` : ""}`,
      action:
        "Consider having a small snack or reducing next meal dose by 1-2 units",
      currentIob: correctionIob,
      predictedBg: latestBg
        ? latestBg - correctionIob * correctionFactor
        : undefined,
      predictionSource: "history-supported",
      supportStrength: historySupportStrength,
      supportNote: historySupportNote,
      priority: 1,
    });
  }

  // Post-meal spike prediction
  const mealPattern = historicalPatterns.find(
    (p) => p.type === "post-meal" && p.affectedPeriods?.includes(currentPeriod),
  );
  if (mealPattern && latestBg && latestBg > 7) {
    predictions.push({
      type: "hyper-risk",
      timeframe: "next 1-3 hours",
      likelihood: mealPattern.severity === "action" ? "high" : "medium",
      basedOn: `Historical post-${PERIOD_TAG_INFO[currentPeriod].label.toLowerCase()} spikes detected. ${mealPattern.detail}`,
      action: mealPattern.dafneRecommendation,
      predictionSource: "history-supported",
      supportStrength: historySupportStrength,
      supportNote: historySupportNote,
      priority: 3,
    });
  }

  // Dawn phenomenon prediction (if evening/bedtime)
  if (
    (currentPeriod === "BT" || currentPeriod === "EM") &&
    historicalPatterns.some((p) => p.type === "dawn")
  ) {
    predictions.push({
      type: "hyper-risk",
      timeframe: "tomorrow morning (5-8 AM)",
      likelihood: "medium",
      basedOn: "Dawn phenomenon pattern detected in recent data",
      action:
        "If GL is in-range at bedtime, no action needed. If elevated, consider small correction.",
      predictionSource: "history-supported",
      supportStrength: historySupportStrength,
      supportNote: historySupportNote,
      priority: 7,
    });
  }

  // Default source for predictions that are generated from today's context
  for (const prediction of predictions) {
    if (!prediction.predictionSource) {
      prediction.predictionSource = "today-only";
    }
  }

  // Deduplicate same-type cards with same timeframe/source, keep highest-priority item
  const deduped = new Map<string, PredictionResult>();
  for (const prediction of predictions) {
    const key = `${prediction.type}|${prediction.timeframe}|${prediction.predictionSource}`;
    const existing = deduped.get(key);
    if (!existing || (prediction.priority ?? 99) < (existing.priority ?? 99)) {
      deduped.set(key, prediction);
    }
  }

  let finalPredictions = [...deduped.values()];

  // Avoid conflicting guidance: if a hypo risk is active, hide correction-effect info cards
  if (finalPredictions.some((p) => p.type === "hypo-risk")) {
    finalPredictions = finalPredictions.filter(
      (p) => p.type !== "correction-effect",
    );
  }

  // Sort by priority (lower = more important)
  finalPredictions.sort((a, b) => (a.priority ?? 99) - (b.priority ?? 99));

  return finalPredictions;
}

/**
 * Get the current period based on time of day
 */
export function getCurrentPeriod(): PeriodTag {
  const hour = new Date().getHours();
  if (hour >= 0 && hour < 5) return "3AM";
  if (hour >= 5 && hour < 10) return "B";
  if (hour >= 10 && hour < 15) return "L";
  if (hour >= 15 && hour < 21) return "EM";
  return "BT";
}
